import { defineMessages, type IntlShape } from 'react-intl';

import { type AgentIconType } from '@confluence/agent-user-dual-avatar/entry-points/agentIconType.ts';
import {
	getAgentBrand as getSharedAgentBrand,
	type AgentBrand,
} from '@confluence/agent-user-dual-avatar/entry-points/getAgentBrand.ts';
import { expVal } from '@confluence/feature-experiments';

import { toUsableIdentityAccountId } from '../toUsableIdentityAccountId.ts';

/**
 * Shared, query-agnostic classification core for AI-agent page views.
 *
 * Both the Page Analytics viewers table (`agentViewers.ts`) and the byline
 * analytics dialog (`usePageViews.ts`) read agent page views from
 * `ContentAnalyticsPageViewInfo` but shape them into different row models. The
 * *rules* for detecting agents, grouping their views, and resolving an agent's
 * display name + fallback icon are identical, so they live here as the single
 * source of truth.
 *
 * Agents carry no resolvable account id in the analytics data, so identity is
 * resolved entirely locally:
 *   - Source agents ("Atlassian MCP", "Teamwork Graph CLI") → treated as
 *     external/unidentified: folded into the "External agents" row + AI-agent icon.
 *   - Resolved (has `userProfile.displayName`) → that name, hexagon avatar,
 *     grouped by `displayName` (so the same agent under different account ids
 *     consolidates into one row).
 *   - Resolved to a known brand (Cursor, Claude, Claude Code, ChatGPT, Figma, Dia,
 *     Codex) → the brand name + brand icon, grouped by brand, so a product's
 *     per-integration accounts ("Claude Code (atlassian-mcp)", "…-2") read as one row.
 *   - Unresolved + `agentType === 'rovo_chat'` → "Rovo" + Rovo icon.
 *   - Unresolved (any other agentType) → "External agents" (aggregated) or
 *     "External agent" (a single agent attributed to a named person) + AI-agent icon.
 * Unresolved agents of the same type roll up into a single fallback row.
 */

const i18n = defineMessages({
	unknownAgents: {
		id: 'confluence-analytics.agent-classification.unknown-agents',
		defaultMessage: 'External agents',
		description:
			'Row label in Confluence analytics tables for the aggregated row of AI agents that acted but could not be resolved to a specific agent account (external agents without unique identifiers).',
	},
	unknownAgent: {
		id: 'confluence-analytics.agent-classification.unknown-agent',
		defaultMessage: 'External agent',
		description:
			'Row label in Confluence analytics tables for a single unresolved AI agent attributed to a named person, e.g. "External agent" with a "with {name}" subtitle.',
	},
});

/** Localised label for a single unresolved external agent attributed to a named person. */
export const getUnknownAgentSingularName = (formatMessage: IntlShape['formatMessage']): string =>
	formatMessage(i18n.unknownAgent);

/**
 * Raw `agentType` the backend reports for Rovo chat agent views that carry no
 * resolvable account. Any other unresolved agentType (e.g. convo-ai) falls back
 * to the generic "External agents" label + AI-agent icon.
 */
export const AGENT_TYPE_ROVO_CHAT = 'rovo_chat';

const ATLASSIAN_MCP_AGENT_DISPLAY_NAME = 'Atlassian MCP';

const TEAMWORK_GRAPH_CLI_AGENT_DISPLAY_NAME = 'Teamwork Graph CLI';

/**
 * Resolved agent display name (from the agent-details query's `name` field) that
 * should render the Cursor brand icon in place of the account `picture`. Cursor
 * agents resolve to a real account with a name + picture, so — unlike the
 * unresolved Rovo/AI-agent fallbacks — this is matched on the resolved name.
 */
export const AGENT_NAME_CURSOR = 'Cursor';

/**
 * Resolved agent display names that should render a brand icon in place of the
 * account `picture`, matched the same way as {@link AGENT_NAME_CURSOR}.
 */
export const AGENT_NAME_CLAUDE = 'Claude';

/**
 * Claude Code registers one agent account per integration, so it resolves to names like
 * "Claude Code (atlassian-mcp)" and "Claude Code (atlassian-mcp-2)" rather than a bare
 * "Claude". They are all the same product, so every variant renders the Claude brand
 * icon and rolls up into a single "Claude Code" row — see {@link getAgentBrand}.
 */
export const AGENT_NAME_CLAUDE_CODE = 'Claude Code';

export const AGENT_NAME_CHATGPT = 'ChatGPT';

export const AGENT_NAME_FIGMA = 'Figma';

export const AGENT_NAME_DIA = 'Dia';

export const AGENT_NAME_CODEX = 'Codex';

/** Group-key prefix for unresolved agents. */
const FALLBACK_KEY_PREFIX = '__fallback__:';

/**
 * Group key for unresolved Rovo chat agents — kept separate so they render as a
 * single "Rovo" row rather than folding into the generic "External agents" row.
 */
export const FALLBACK_KEY_ROVO: '__fallback__:rovo_chat' = `${FALLBACK_KEY_PREFIX}${AGENT_TYPE_ROVO_CHAT}`;

/**
 * Group key for every other unresolved agent, regardless of `agentType`, so they
 * all roll up into a single "External agents" row.
 */
export const FALLBACK_KEY_UNKNOWN: '__fallback__:unknown' = `${FALLBACK_KEY_PREFIX}unknown`;

/**
 * Minimal page-view shape needed to classify an agent view. Both the viewers
 * table and byline generated query types structurally satisfy this.
 */
export type AgentClassifiablePageView = {
	accountType?: string | null;
	agentType?: string | null;
	userProfile?: {
		id?: string | null;
		displayName?: string | null;
		avatarUrl?: string | null;
		photos?:
			| readonly ({
					value?: string | null;
					isPrimary?: boolean | null;
			  } | null)[]
			| null;
	} | null;
};

export type AgentTypeBrandMapping = {
	kind: 'brand';
	name: string;
	agentIcon: AgentIconType;
	groupKey: string;
};

export type AgentTypeExternalMapping = {
	kind: 'external';
};

export type AgentTypeMapping = AgentTypeBrandMapping | AgentTypeExternalMapping;

const AGENT_NAME_GROK = 'Grok';
const AGENT_NAME_GITHUB_COPILOT = 'GitHub Copilot';
const AGENT_NAME_VISUAL_STUDIO_CODE = 'Visual Studio Code';
const AGENT_NAME_OPEN_CODE = 'Open Code';
const AGENT_NAME_HERMES_AGENT = 'Hermes Agent';
const AGENT_NAME_KIRO = 'Kiro';
const AGENT_NAME_LOVABLE = 'Lovable';
const AGENT_NAME_REPLIT = 'Replit';

const PRIORITY_AGENT_TYPE_KEYS = new Set(['figma', 'lovable', 'replit']);

export const isPriorityAgentType = (agentType: string | null | undefined): boolean => {
	const normalized = agentType?.trim().toLowerCase();
	return Boolean(normalized && PRIORITY_AGENT_TYPE_KEYS.has(normalized));
};

const KNOWN_AGENT_TYPE_BRANDS: Readonly<Record<string, Omit<AgentTypeBrandMapping, 'kind'>>> = {
	claude: { name: AGENT_NAME_CLAUDE, agentIcon: 'claude', groupKey: AGENT_NAME_CLAUDE },
	codex: { name: AGENT_NAME_CODEX, agentIcon: 'codex', groupKey: AGENT_NAME_CODEX },
	cursor: { name: AGENT_NAME_CURSOR, agentIcon: 'cursor', groupKey: AGENT_NAME_CURSOR },
	chatgpt: { name: AGENT_NAME_CHATGPT, agentIcon: 'chatgpt', groupKey: AGENT_NAME_CHATGPT },
	dia: { name: AGENT_NAME_DIA, agentIcon: 'dia', groupKey: AGENT_NAME_DIA },
	figma: { name: AGENT_NAME_FIGMA, agentIcon: 'figma', groupKey: AGENT_NAME_FIGMA },
	lovable: { name: AGENT_NAME_LOVABLE, agentIcon: 'lovable', groupKey: AGENT_NAME_LOVABLE },
	replit: { name: AGENT_NAME_REPLIT, agentIcon: 'replit', groupKey: AGENT_NAME_REPLIT },
	grok: { name: AGENT_NAME_GROK, agentIcon: 'grok', groupKey: AGENT_NAME_GROK },
	github_copilot: {
		name: AGENT_NAME_GITHUB_COPILOT,
		agentIcon: 'github-copilot',
		groupKey: AGENT_NAME_GITHUB_COPILOT,
	},
	vscode: {
		name: AGENT_NAME_VISUAL_STUDIO_CODE,
		agentIcon: 'vscode',
		groupKey: AGENT_NAME_VISUAL_STUDIO_CODE,
	},
	open_code: { name: AGENT_NAME_OPEN_CODE, agentIcon: 'open-code', groupKey: AGENT_NAME_OPEN_CODE },
	hermes: { name: AGENT_NAME_HERMES_AGENT, agentIcon: 'hermes', groupKey: AGENT_NAME_HERMES_AGENT },
	kiro: { name: AGENT_NAME_KIRO, agentIcon: 'kiro', groupKey: AGENT_NAME_KIRO },
	[AGENT_TYPE_ROVO_CHAT]: { name: 'Rovo', agentIcon: 'rovo', groupKey: FALLBACK_KEY_ROVO },
};

const EXTERNAL_AGENT_TYPES = new Set(['twg', 'mcp']);

/**
 * A page view attributed to an AI agent rather than a human. Canonical signal is
 * `accountType === 'AGENT'`; a non-empty `agentType` is a secondary signal.
 * Deliberately does NOT use `permissionType === APP` (the broad "app-backed
 * principal" flag, which can false-positive on non-agent apps).
 */
export const isAgentPageView = (pageView: AgentClassifiablePageView): boolean =>
	pageView.accountType === 'AGENT' || (pageView.agentType != null && pageView.agentType !== '');

/** A view is "resolved" when it carries an agent account with a display name. */
export const isResolvedAgent = (pageView: AgentClassifiablePageView): boolean =>
	!!pageView.userProfile?.displayName;

/**
 * The minimal shape needed to spot an agent record attributed to its own
 * account. Covers all three record shapes: the `activityByUser` agent record
 * (`accountId` + `relatedUserAccountId`) and the page-view records, which carry
 * identity on `userProfile` and may report the related account as either
 * `relatedUserAccountId` or a `relatedUser` object.
 */
export type SelfAttributableAgentRecord = {
	accountId?: string | null;
	userProfile?: { id?: string | null } | null;
	relatedUserAccountId?: string | null;
	relatedUser?: { id?: string | null } | null;
};

/**
 * An agent record attributed to its own account. An autonomous agent run has no
 * human actor, so the underlying analytics event is emitted with `user_id` set
 * to the agent's own account id, which surfaces here as a related account equal
 * to the agent's own. Such a record is *not* "an agent acted on behalf of a
 * person": rendering it as one would stack the agent's avatar on top of itself
 * and repeat its name in the "with {name}" subtitle. Callers drop the related
 * account for these records so they render as a single agent identity, the same
 * way a regular user row does.
 */
export const isSelfAttributedAgentRecord = (record: SelfAttributableAgentRecord): boolean => {
	const relatedAccountId = record.relatedUserAccountId ?? record.relatedUser?.id;
	if (!relatedAccountId) {
		return false;
	}
	return relatedAccountId === record.userProfile?.id || relatedAccountId === record.accountId;
};

export const getAgentTypeMapping = (
	agentType: string | null | undefined,
): AgentTypeMapping | undefined => {
	const normalized = agentType?.trim().toLowerCase();
	if (!normalized) {
		return undefined;
	}
	if (EXTERNAL_AGENT_TYPES.has(normalized)) {
		return { kind: 'external' };
	}
	const brand = KNOWN_AGENT_TYPE_BRANDS[normalized];
	if (brand) {
		return { kind: 'brand', ...brand };
	}
	return undefined;
};

/**
 * Source agents (e.g. Atlassian MCP, Teamwork Graph CLI) resolve to a display
 * name but represent activity from agents without a unique identifier, so they
 * are treated as external/unidentified rather than named agents.
 */
const isUnidentifiedSourceAgent = (pageView: AgentClassifiablePageView): boolean =>
	pageView.userProfile?.displayName === ATLASSIAN_MCP_AGENT_DISPLAY_NAME ||
	pageView.userProfile?.displayName === TEAMWORK_GRAPH_CLI_AGENT_DISPLAY_NAME;

/**
 * Analytics handles the exact "Rovo" name separately under ai_agent_analytics.
 * Exclude Rovo brand matches here to preserve that policy and keep suffixed Rovo
 * accounts grouped by their own display name, with their own avatar.
 */
const getAgentBrand = (displayName: string): AgentBrand | undefined => {
	const brand = getSharedAgentBrand(displayName);
	return brand?.agentIcon === 'rovo' ? undefined : brand;
};

const getProfileAvatarUrl = (
	userProfile: AgentClassifiablePageView['userProfile'],
): string | undefined =>
	userProfile?.avatarUrl ??
	userProfile?.photos?.find((photo) => photo?.isPrimary)?.value ??
	userProfile?.photos?.find(Boolean)?.value ??
	undefined;

const ROVO_FALLBACK_DISPLAY_NAME = 'Rovo';

/**
 * Grouping key for an agent view. Source agents (Atlassian MCP, Teamwork Graph
 * CLI) fold into the "External agents" bucket. A resolved agent that belongs to a known
 * brand groups by that brand name, so every per-integration account of one product
 * ("Claude Code (atlassian-mcp)", "Claude Code (atlassian-mcp-2)", …) consolidates into a
 * single "Claude Code" row. Any other resolved agent groups by
 * its display name so that the same agent surfaced under multiple account ids
 * (e.g. per-instance or per-deployment agent accounts that share one name)
 * consolidates into a single row rather than one row per id. Unresolved agents
 * roll up into just two rows: one "Rovo" row (agentType `rovo_chat`) and one
 * "External agents" row for every other unresolved agent, regardless of type.
 */
export const agentGroupKey = (pageView: AgentClassifiablePageView): string => {
	const typeMapping = getAgentTypeMapping(pageView.agentType);
	if (typeMapping?.kind === 'external') {
		return FALLBACK_KEY_UNKNOWN;
	}
	if (
		typeMapping?.kind === 'brand' &&
		isPriorityAgentType(pageView.agentType) &&
		expVal('ai_analytics_priority_types', 'isEnabled', false)
	) {
		return typeMapping.groupKey;
	}
	if (
		typeMapping?.kind === 'brand' &&
		!isPriorityAgentType(pageView.agentType) &&
		expVal('ai_analytics_agent_types', 'isEnabled', false)
	) {
		return typeMapping.groupKey;
	}
	if (isUnidentifiedSourceAgent(pageView)) {
		return FALLBACK_KEY_UNKNOWN;
	}
	if (isResolvedAgent(pageView)) {
		const displayName = pageView.userProfile?.displayName ?? '';
		if (
			displayName === ROVO_FALLBACK_DISPLAY_NAME &&
			expVal('ai_agent_analytics', 'isEnabled', false)
		) {
			return FALLBACK_KEY_ROVO;
		}
		return getAgentBrand(displayName)?.name ?? displayName;
	}
	return pageView.agentType === AGENT_TYPE_ROVO_CHAT ? FALLBACK_KEY_ROVO : FALLBACK_KEY_UNKNOWN;
};

/** Whether a group key is the generic "External agents" bucket. */
export const isUnknownAgentGroupKey = (agentGroupKeyValue: string): boolean =>
	agentGroupKeyValue === FALLBACK_KEY_UNKNOWN;

/** Whether a group key is the unidentified/external-agent bucket. */
export const isUnidentifiedAgentGroupKey = (agentGroupKeyValue: string): boolean =>
	agentGroupKeyValue === FALLBACK_KEY_UNKNOWN;

export type ClickableAgentIdentity = {
	accountId?: string | null;
	agentIcon?: AgentIconType;
	groupKey?: string;
	displayName?: string;
};

export const isClickableAgentIdentity = ({
	accountId,
	agentIcon,
	groupKey,
	displayName,
}: ClickableAgentIdentity): boolean => {
	const isAriAccountId = Boolean(accountId?.startsWith('ari:'));
	if (!isAriAccountId && !toUsableIdentityAccountId(accountId)) {
		return false;
	}
	if (agentIcon) {
		return false;
	}
	if (groupKey && (isUnidentifiedAgentGroupKey(groupKey) || groupKey === FALLBACK_KEY_ROVO)) {
		return false;
	}
	if (displayName && (getAgentBrand(displayName) || displayName === ROVO_FALLBACK_DISPLAY_NAME)) {
		return false;
	}
	return true;
};

/**
 * Resolves the display name and fallback icon for a group of an agent's views.
 * Source agents (Atlassian MCP, Teamwork Graph CLI) resolve to the "External
 * agents" label + AI-agent icon. Other resolved agents use their real name and,
 * when the agent's account resolved to
 * a `picture`, that avatar URL (rendered inside the hexagon); otherwise they use
 * the deterministic hexagon avatar (no `agentIcon`). The exceptions are the known
 * brands (Cursor, Claude, Claude Code, ChatGPT, Figma, Dia, Codex), which render their
 * brand icon instead of the account `picture` and are named after the brand rather than
 * the account, so a group of per-integration accounts reads as one product. An unresolved
 * agent falls back to "Rovo"/rovo icon (rovo_chat) or "External agents"/ai-agent icon
 * (any other type).
 */
export const resolveAgentNameAndIcon = (
	records: readonly AgentClassifiablePageView[],
	formatMessage: IntlShape['formatMessage'],
): { name: string; agentIcon?: AgentIconType; avatarUrl?: string } => {
	const typeMapping = getAgentTypeMapping(records[0]?.agentType);
	if (records.some((record) => getAgentTypeMapping(record.agentType)?.kind === 'external')) {
		return { name: formatMessage(i18n.unknownAgents), agentIcon: 'ai-agent' };
	}
	if (
		typeMapping?.kind === 'brand' &&
		isPriorityAgentType(records[0]?.agentType) &&
		expVal('ai_analytics_priority_types', 'isEnabled', false)
	) {
		return { name: typeMapping.name, agentIcon: typeMapping.agentIcon };
	}
	if (
		typeMapping?.kind === 'brand' &&
		!isPriorityAgentType(records[0]?.agentType) &&
		expVal('ai_analytics_agent_types', 'isEnabled', false)
	) {
		return { name: typeMapping.name, agentIcon: typeMapping.agentIcon };
	}

	const resolvedProfile = records
		.filter((record) => !isUnidentifiedSourceAgent(record))
		.find((record) => record.userProfile?.displayName)?.userProfile;
	if (records.some(isUnidentifiedSourceAgent)) {
		return { name: formatMessage(i18n.unknownAgents), agentIcon: 'ai-agent' };
	}
	if (resolvedProfile?.displayName) {
		// The Cursor/Claude/Claude Code/ChatGPT/Figma/Dia/Codex agents resolve to a real
		// account, but we show the brand name + brand icon rather than the account's own name
		// and `picture`, so per-integration variants (e.g. "Claude Code (atlassian-mcp)") read
		// as the single product row they are grouped into by `agentGroupKey`.
		const brand = getAgentBrand(resolvedProfile.displayName);
		if (brand) {
			return { name: brand.name, agentIcon: brand.agentIcon };
		}
		if (
			resolvedProfile.displayName === ROVO_FALLBACK_DISPLAY_NAME &&
			expVal('ai_agent_analytics', 'isEnabled', false)
		) {
			return { name: ROVO_FALLBACK_DISPLAY_NAME, agentIcon: 'rovo' };
		}
		return { name: resolvedProfile.displayName, avatarUrl: getProfileAvatarUrl(resolvedProfile) };
	}
	if (records[0]?.agentType === AGENT_TYPE_ROVO_CHAT) {
		return { name: 'Rovo', agentIcon: 'rovo' };
	}
	return { name: formatMessage(i18n.unknownAgents), agentIcon: 'ai-agent' };
};
